import React, { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import JsBarcode from 'jsbarcode';
import { formatCurrency } from '../utils/currency';

interface InventoryItem {
  id: number;
  inventory_id: string;
  phone_model: string;
  imei_mac_address: string;
  category: string;
  buying_price: number;
  selling_price: number;
  status: string;
  branch_name?: string;
  supplier?: {
    id: number;
    name: string;
    phone_number: string;
    aadhar?: string;
    address?: string;
  };
  date_added: string;
  service_expense?: number;
  package_expense?: number;
}

interface InventoryBarcodeModalProps {
  item: InventoryItem;
  isOpen: boolean;
  onClose: () => void;
}

const InventoryBarcodeModal: React.FC<InventoryBarcodeModalProps> = ({
  item,
  isOpen,
  onClose
}) => {
  const barcodeRef = useRef<SVGSVGElement>(null);
  const printBarcodeRef = useRef<SVGSVGElement>(null);

  // Generate barcode when modal opens or item changes
  useEffect(() => {
    if (isOpen && item && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, item.inventory_id, {
          format: "CODE128",
          width: 2,
          height: 60,
          displayValue: false,
          background: "#ffffff",
          lineColor: "#000000"
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    }
  }, [isOpen, item]);

  // Generate print barcode (larger for printing)
  useEffect(() => {
    if (isOpen && item && printBarcodeRef.current) {
      try {
        JsBarcode(printBarcodeRef.current, item.inventory_id, {
          format: "CODE128",
          width: 2,
          height: 50,
          displayValue: false,
          background: "#ffffff",
          lineColor: "#000000"
        });
      } catch (error) {
        console.error('Error generating print barcode:', error);
      }
    }
  }, [isOpen, item]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow && printBarcodeRef.current) {
      const barcodeHTML = printBarcodeRef.current.outerHTML;
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Inventory Barcode - ${item.inventory_id}</title>
          <style>
            @media print {
              body { 
                margin: 0; 
                padding: 10px; 
                font-family: Arial, sans-serif;
              }
              .barcode-label { 
                width: 2.2in;
                height: 1.2in;
                border: 1px solid #000;
                border-radius: 4px;
                background: white;
                padding: 3px;
                position: relative;
                page-break-inside: avoid;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
              }
              .label-header {
                text-align: center;
                font-weight: bold;
                font-size: 11px;
                margin-bottom: 2px;
                text-transform: uppercase;
                line-height: 1;
              }
              .label-content {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                flex: 1;
              }
              .label-content svg {
                width: 90%;
                max-width: 100%;
                height: auto;
              }
              .inventory-id {
                font-size: 10px;
                font-family: monospace;
                font-weight: bold;
                margin-bottom: 2px;
                line-height: 1;
              }
              .barcode-number {
                font-size: 9px;
                margin-top: 2px;
                font-family: monospace;
                line-height: 1;
              }
            }
            body { 
              margin: 0; 
              padding: 10px; 
              font-family: Arial, sans-serif;
            }
            .barcode-label { 
              width: 2.2in;
              height: 1.2in;
              border: 1px solid #000;
              border-radius: 4px;
              background: white;
              padding: 3px;
              position: relative;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
            .label-header {
              text-align: center;
              font-weight: bold;
              font-size: 11px;
              margin-bottom: 2px;
              text-transform: uppercase;
              line-height: 1;
            }
            .label-content {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              flex: 1;
            }
            .label-content svg {
              width: 90%;
              max-width: 100%;
              height: auto;
            }
            .inventory-id {
              font-size: 10px;
              font-family: monospace;
              font-weight: bold;
              margin-bottom: 2px;
              line-height: 1;
            }
            .barcode-number {
              font-size: 9px;
              margin-top: 2px;
              font-family: monospace;
              line-height: 1;
            }
          </style>
        </head>
        <body>
          <div class="barcode-label">
            <div class="label-header">${item.phone_model}</div>
            <div class="label-content">
              <div class="inventory-id">${item.inventory_id}</div>
              ${barcodeHTML}
              <div class="barcode-number">${item.imei_mac_address}</div>
            </div>
          </div>
        </body>
        </html>
      `);
      
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ready': return 'bg-green-100 text-green-800';
      case 'reserved': return 'bg-yellow-100 text-yellow-800';
      case 'service': return 'bg-blue-100 text-blue-800';
      case 'sold': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ready': return 'Ready';
      case 'reserved': return 'Reserved';
      case 'service': return 'In Service';
      case 'sold': return 'Sold';
      default: return status;
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Inventory Barcode</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Barcode Section */}
        <div className="p-6">
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">Barcode Label Preview</h3>
            
            {/* Custom Barcode Label Preview */}
            <div className="bg-white rounded-lg p-6 mb-6 shadow-sm border max-w-md mx-auto">
              <div className="border-2 border-black rounded-lg p-2 bg-white" style={{aspectRatio: '2.2/1.2'}}>
                {/* Label Header */}
                <div className="text-center font-bold text-sm mb-1 uppercase">
                  {item.phone_model}
                </div>
                
                {/* Label Content */}
                <div className="flex flex-col items-center justify-center h-full">
                  {/* Inventory ID */}
                  <div className="text-sm font-mono font-bold mb-1">
                    {item.inventory_id}
                  </div>
                  
                  {/* Barcode */}
                  <div className="bg-white rounded mb-1 w-full overflow-hidden">
                    <svg ref={barcodeRef} className="mx-auto" style={{maxWidth: '100%', width: '90%', height: 'auto'}}></svg>
                  </div>
                  
                  {/* IMEI/MAC */}
                  <div className="text-sm font-mono font-medium">
                    {item.imei_mac_address}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Print Button */}
            <div className="text-center">
              <button
                onClick={handlePrint}
                className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print Barcode Label
              </button>
            </div>
          </div>

          {/* Item Details */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Item Details</h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Inventory ID</label>
                    <p className="text-sm font-semibold text-gray-900 bg-gray-50 px-3 py-2 rounded">{item.inventory_id}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Model</label>
                    <p className="text-sm text-gray-900">{item.phone_model}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">IMEI/Mac Address</label>
                    <p className="text-sm text-gray-900 font-mono">{item.imei_mac_address}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <p className="text-sm text-gray-900 capitalize">{item.category.replace('_', ' ')}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(item.status)}`}>
                      {getStatusLabel(item.status)}
                    </span>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Buying Price</label>
                    <p className="text-sm text-gray-900 font-semibold">{formatCurrency(item.buying_price)}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Selling Price</label>
                    <p className="text-sm text-gray-900 font-semibold text-green-600">{formatCurrency(item.selling_price)}</p>
                  </div>
                  
                  {item.service_expense && item.service_expense > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Service Expense</label>
                      <p className="text-sm text-gray-900">{formatCurrency(item.service_expense)}</p>
                    </div>
                  )}
                  
                  {item.package_expense && item.package_expense > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Package Expense</label>
                      <p className="text-sm text-gray-900">{formatCurrency(item.package_expense)}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                    <p className="text-sm text-gray-900">{item.branch_name || 'N/A'}</p>
                  </div>
                  
                  {item.supplier?.name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                      <p className="text-sm text-gray-900">{item.supplier.name}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Added</label>
                    <p className="text-sm text-gray-900">{new Date(item.date_added).toLocaleDateString('en-IN')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Supplier Details */}
          {item.supplier && (item.supplier.name || item.supplier.phone_number || item.supplier.aadhar || item.supplier.address) && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mt-6">
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Supplier Details</h3>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    {item.supplier.name && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Name</label>
                        <p className="text-sm text-gray-900">{item.supplier.name}</p>
                      </div>
                    )}
                    
                    {item.supplier.phone_number && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                        <p className="text-sm text-gray-900 font-mono">{item.supplier.phone_number}</p>
                      </div>
                    )}
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    {item.supplier.aadhar && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Number</label>
                        <p className="text-sm text-gray-900 font-mono">{item.supplier.aadhar}</p>
                      </div>
                    )}
                    
                    {item.supplier.address && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                        <p className="text-sm text-gray-900">{item.supplier.address}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Hidden barcode for printing */}
        <div style={{ display: 'none' }}>
          <svg ref={printBarcodeRef}></svg>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InventoryBarcodeModal;